import React, { useState, useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  AudioLines,
  BarChart2,
  BookOpenText,
  Library,
  MessageSquare,
  ShieldCheck,
  UserRound,
} from "lucide-react";
import { HamburgerMenuIcon, Cross2Icon } from "@radix-ui/react-icons";
import logo from "@/assets/logo_noborder.png";
import useStore from "@/store";
import { isFounderEmail } from "@/constants/founders";

const BASE_NAV_ITEMS = [
  { label: "Learn", to: "/", icon: AudioLines },
  {
    label: "Collections",
    to: "/collections",
    icon: Library,
  },
  { label: "Stats", to: "/stats", icon: BarChart2 },
  { label: "Forum", to: "/forum", icon: MessageSquare },
];

const ACCOUNT_ITEM = { label: "Account", to: "/account", icon: UserRound };

// Support lives on the Account page; only the docs link stays here.
const supportItems = [
  {
    label: "Docs",
    href: "https://speechcraft.io/docs/intro/",
    icon: BookOpenText,
  },
];

const classNames = (...classes) => classes.filter(Boolean).join(" ");

function useMobileQuery() {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return isMobile;
}

const Sidebar = () => {
  const isMobile = useMobileQuery();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const { pathname } = useLocation();
  const { user } = useStore().user;

  const isOwner = isFounderEmail(user?.email);

  const navItems = useMemo(() => {
    const items = [...BASE_NAV_ITEMS];
    if (isOwner) {
      items.push({
        label: "Founder",
        to: "/founder",
        icon: ShieldCheck,
      });
    }
    return items;
  }, [isOwner]);

  const toggleMobileSidebar = () => setIsMobileOpen((prev) => !prev);
  const closeMobileSidebar = () => setIsMobileOpen(false);

  const renderIcon = (Icon) => (
    <Icon size={isMobile ? 18 : 20} strokeWidth={1.6} aria-hidden="true" />
  );

  const renderNavItem = (item) => {
    const isActive =
      item.to === "/"
        ? pathname === item.to
        : pathname === item.to || pathname.startsWith(`${item.to}/`);

    return (
      <Link
        key={item.to}
        to={item.to}
        onClick={() => {
          if (isMobile) {
            closeMobileSidebar();
          }
        }}
        aria-current={isActive ? "page" : undefined}
        className={classNames(
          "group relative flex w-full items-center rounded-lg transition-all duration-200",
          isMobile ? "gap-3 px-3 py-2.5" : "flex-col gap-1.5 px-1 py-2.5",
          isActive
            ? "bg-gradient-to-b from-primary/15 to-primary/5 text-primary ring-1 ring-inset ring-primary/25"
            : "text-fg-muted hover:bg-surface hover:text-fg"
        )}
      >
        {isActive && (
          <span
            className={classNames(
              "pointer-events-none absolute rounded-full bg-primary shadow-glow-sm",
              isMobile ? "-left-3 top-2.5 bottom-2.5 w-[3px]" : "-left-2 top-3 bottom-3 w-[3px]"
            )}
            aria-hidden="true"
          />
        )}
        <span
          className={classNames(
            "flex items-center justify-center transition-transform duration-200",
            isActive
              ? "drop-shadow-[0_0_8px_rgb(var(--primary)/0.7)]"
              : "group-hover:scale-110"
          )}
        >
          {renderIcon(item.icon)}
        </span>
        <span
          className={classNames(
            "font-medium",
            isMobile ? "text-sm" : "text-[10px] leading-none tracking-wide whitespace-nowrap"
          )}
        >
          {item.label}
        </span>
      </Link>
    );
  };

  const renderSupportLink = (item) => (
    <a
      key={item.label}
      href={item.href}
      target={item.href.startsWith("http") ? "_blank" : "_self"}
      rel="noopener noreferrer"
      className={classNames(
        "group flex w-full items-center rounded-lg text-fg-subtle transition-colors duration-200 hover:bg-surface hover:text-accent",
        isMobile ? "gap-3 px-3 py-2.5" : "flex-col gap-1.5 px-1 py-2"
      )}
      onClick={() => {
        if (isMobile) {
          closeMobileSidebar();
        }
      }}
    >
      {renderIcon(item.icon)}
      <span
        className={classNames(
          "font-medium",
          isMobile ? "text-sm" : "text-[10px] leading-none tracking-wide"
        )}
      >
        {item.label}
      </span>
    </a>
  );

  return (
    <>
      {isMobile && (
        <>
          {/* Phone top bar: menu button + brand, so nothing floats over content. */}
          <header className="fixed inset-x-0 top-0 z-50 flex h-14 items-center gap-3 border-b border-line bg-canvas/85 px-4 backdrop-blur">
            <button
              type="button"
              className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-line text-fg transition hover:border-primary/50 hover:text-primary"
              onClick={toggleMobileSidebar}
              aria-label="Toggle navigation"
              aria-expanded={isMobileOpen}
            >
              {isMobileOpen ? <Cross2Icon className="h-5 w-5" /> : <HamburgerMenuIcon className="h-5 w-5" />}
            </button>
            <Link to="/" onClick={closeMobileSidebar} className="flex items-center gap-2">
              <img src={logo} alt="" className="h-7 w-7 object-contain" />
              <span className="font-display text-base font-bold tracking-wide text-fg">SpeechCraft</span>
            </Link>
          </header>
          {isMobileOpen && (
            <div
              className="fixed inset-0 z-30 bg-canvas/60 backdrop-blur-sm"
              onClick={closeMobileSidebar}
              aria-hidden="true"
            />
          )}
        </>
      )}

      <aside
        className={classNames(
          "z-40 flex md:h-screen flex-col border-r border-line bg-gradient-to-b from-surface to-canvas text-fg transition-transform duration-300 ease-out",
          isMobile
            ? `fixed bottom-0 left-0 top-14 w-64 px-5 ${isMobileOpen ? "translate-x-0" : "-translate-x-full"}`
            : "hidden md:flex md:w-[84px] md:px-2.5 sticky top-0"
        )}
      >
        <div className={classNames("flex flex-1 flex-col justify-between", isMobile ? "pb-6 pt-6" : "py-6")}>
          <div className={classNames("flex flex-col gap-6", isMobile ? "items-stretch" : "items-center")}>
            {!isMobile && (
              <img
                src={logo}
                alt="SpeechCraft"
                className="h-11 w-11 object-contain drop-shadow-[0_0_12px_rgb(var(--primary)/0.4)]"
              />
            )}

            <nav className="flex w-full flex-col gap-1">{navItems.map(renderNavItem)}</nav>
          </div>

          <div className="flex w-full flex-col gap-1">
            <div className={classNames("mb-2 h-px bg-line", isMobile ? "w-full" : "mx-auto w-8")} />
            {supportItems.map(renderSupportLink)}
            {renderNavItem(ACCOUNT_ITEM)}
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
